if (!customElements.get('ug-jp-campaign-catalog')) {
  customElements.define('ug-jp-campaign-catalog', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = Array.from(this.querySelectorAll('[data-catalog-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-catalog-panel]'));
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-catalog-tab]');
        if (tab && this.contains(tab)) this.select(this.tabs.indexOf(tab));
      }, options);
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-catalog-tab]');
        const index = this.tabs.indexOf(tab);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.select(next);
        this.tabs[next].focus({ preventScroll: true });
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const tab = event.target.closest('[data-catalog-tab]');
        if (tab) return this.select(this.tabs.indexOf(tab));
        const panel = event.target.closest('[data-catalog-panel]');
        if (panel) this.select(this.panels.indexOf(panel));
      }, options);
      if (this.matches('.ug-jpcatalog--nas, .ug-jpcatalog--accessories')) this.startRowAlignment(options);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.rowResizeObserver?.disconnect();
      this.rowMutationObserver?.disconnect();
      if (this.rowFrame != null) cancelAnimationFrame(this.rowFrame);
      this.rowFrame = null;
      if (this.rowAlignmentEnabled) this.clearRowHeights();
      this.rowAlignmentEnabled = false;
      this.rowWidth = null;
    }
    startRowAlignment(options) {
      const schedule = () => this.queueRowAlignment();
      this.rowFrame = null;
      this.rowWidth = null;
      this.rowAlignmentEnabled = true;
      window.addEventListener('resize', schedule, options);
      this.addEventListener('load', schedule, { ...options, capture: true });
      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (!options.signal.aborted) schedule();
        });
        document.fonts.addEventListener('loadingdone', schedule, options);
      }
      this.rowResizeObserver = new ResizeObserver(([entry]) => {
        // Our min-heights change the container's height; only width needs a new pass.
        if (entry.contentRect.width === this.rowWidth) return;
        this.rowWidth = entry.contentRect.width;
        schedule();
      });
      this.rowResizeObserver.observe(this.querySelector('.ug-jpcatalog__inner') || this);
      this.rowMutationObserver = new MutationObserver((records) => {
        // Ignore the wrapper style writes below, but include asynchronously rendered reviews.
        if (records.some((record) => record.type !== 'attributes' || record.attributeName !== 'style' || !record.target.matches('[data-catalog-card]'))) schedule();
      });
      this.rowMutationObserver.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'hidden', 'style', 'data-number-of-reviews'] });
      schedule();
    }
    queueRowAlignment() {
      if (!this.rowAlignmentEnabled || !this.controller || !this.isConnected || this.rowFrame != null) return;
      this.rowFrame = requestAnimationFrame(() => {
        this.rowFrame = null;
        if (this.controller && this.isConnected) this.alignCardRows();
      });
    }
    clearRowHeights() {
      this.querySelectorAll('[data-catalog-card]').forEach((card) => {
        card.style.removeProperty('--jpc-info-height');
        card.style.removeProperty('--jpc-prices-height');
      });
    }
    alignCardRows() {
      this.clearRowHeights();
      if (!this.rowAlignmentEnabled) return;
      const rows = [];
      this.querySelectorAll('.ug-jpcatalog__grid').forEach((grid) => {
        let row = [];
        grid.querySelectorAll('[data-catalog-card]').forEach((card) => {
          const rect = card.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          if (row.length && Math.abs(rect.top - row[0].top) > 1) {
            rows.push(row);
            row = [];
          }
          row.push({
            card,
            top: rect.top,
            info: card.querySelector('.ug-pc-card__info-group')?.getBoundingClientRect().height || 0,
            prices: card.querySelector('.ug-pc-card__prices')?.getBoundingClientRect().height || 0
          });
        });
        if (row.length) rows.push(row);
      });
      // Finish all natural-height reads before setting any row's min-heights.
      rows.forEach((row) => {
        const info = Math.max(...row.map((item) => item.info));
        const prices = Math.max(...row.map((item) => item.prices));
        row.forEach(({ card }) => {
          card.style.setProperty('--jpc-info-height', `${info}px`);
          card.style.setProperty('--jpc-prices-height', `${prices}px`);
        });
      });
    }
    select(index) {
      if (!this.tabs[index] || !this.panels[index]) return;
      if (this.tabs[index].getAttribute('aria-selected') === 'true') return;
      this.tabs.forEach((tab, current) => {
        const active = current === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[current].hidden = !active;
      });
      this.queueRowAlignment();
      this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }
  });
}
